import { DataItem } from 'scrivito'

import { Activity } from '@/Data/Activity/ActivityDataClass'
import { ActivityContactRelation } from '@/Data/ActivityContactRelation/ActivityContactRelationDataClass'
import { ActivityDocumentRelation } from '@/Data/ActivityDocumentRelation/ActivityDocumentRelationDataClass'
import { ActivityProjectRelation } from '@/Data/ActivityProjectRelation/ActivityProjectRelationDataClass'
import { DataScopeOrder } from '@/Data/types'
import { useDataClassIndex } from '@/hooks/useDataClassIndex'
import { getDataItem } from '@/utils/getDataItem'
import { isString } from '@/utils/isString'

interface Args {
  subjectId: string
  search: string
  order: DataScopeOrder
  listSize: number
  activityRelation?: 'contact' | 'project' | 'document'
  /**
   * Activity type selected in the tab filters. Without a selection the list is
   * not restricted by type (CRM-1272).
   */
  type?: string
}

interface ActivityRow {
  id: string
  activity: DataItem
  relation: DataItem
}

export const useActivityTabData = ({
  subjectId,
  search,
  order,
  listSize,
  activityRelation = 'contact',
  type,
}: Args) => {
  const relationClass =
    activityRelation === 'project'
      ? ActivityProjectRelation
      : activityRelation === 'document'
        ? ActivityDocumentRelation
        : ActivityContactRelation
  const subjectKey =
    activityRelation === 'project'
      ? 'project'
      : activityRelation === 'document'
        ? 'document'
        : 'contact'

  const filters: Record<string, string> = {
    [subjectKey]: subjectId,
    activity_search: search,
  }
  // Omitted without a selection: an empty value would be sent as a restriction
  // to `activity_type` and match nothing.
  if (type) {
    filters.activity_type = type
  }

  const {
    dataItems: relations,
    hasMore,
    loadMore,
    count,
  } = useDataClassIndex(relationClass, { filters, order: [order] }, listSize)

  const items: ActivityRow[] = relations.flatMap((relation) => {
    const activityId = relation.get('activity') as string
    if (!isString(activityId)) return []
    const { dataItem: activity } = getDataItem(Activity, activityId)
    if (!activity) return []
    return [{ id: activity.id(), activity, relation }]
  })

  return { items, hasMore, loadMore, count }
}
